<template>
  <div class="inputfile-container" :class="{focus:isFocus, 'input-name':inputName, 'error':error}">
    <div class="inputfile-select" v-if="thumnail === null">
      <input
        type="file"
        :id="name"
        @change="onChange"
        @focus="onFocus"
        @blur="onBlur"
        :disabled="disabled"
      />
      <label :for="name"><i class="fa-solid fa-upload"></i>ファイルを選択</label>
    </div>
    <div class="selected-file" v-else>
      <div class="thumnail">
        <img
          v-show="thumnail"
          class="preview-file"
          :src="thumnail"
        />
      </div>
      <span class="btn-delete" @click="thumnailDelete" v-if="!disabled"></span>
    </div>
    <p class="filename" v-if="inputName">
      <input-text type="text" max-length="128" v-model="fileName" @change="update" :placeholder="nameLabel" :disabled="disabled"></input-text>
    </p>
    <p class="input-error" v-if="error">
      {{ error }}
    </p>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue'
import InputText from './InputText.vue'
import { DialogService } from "@/service/DialogService";

export default defineComponent({
  props: ['modelValue', 'name', 'inputName', 'nameLabel', 'baseUrl','disabled'],
  setup(props, context) {
    if (typeof props.modelValue !== 'object') {
      context.emit('update:modelValue', {})
    }
    const uploadedFile = ref<any>(null)
    let fileName = computed({
      get: () => {
        return (props.modelValue.name) ? props.modelValue.name : null
      },
      set: (val) => {
        if (val === null) {
          delete props.modelValue.name
        } else {
          props.modelValue.name = val
        }
      }
    })
    const thumnail = computed({
      get: () => {
        if (props.modelValue) {
          if (isBase64(props.modelValue.file)) {
            // Base64形式だった場合
            const mime = getMimeByBase64(props.modelValue.file)
            if (mime) {
              if (isImage(mime)) {
                // 画像だった場合はそのまま表示
                return props.modelValue.file
              } else {
                return getIconPath(mime)
              }
            } else {
              return null
            }
          } else {
            if (props.modelValue.file) {
              if (isImage(props.modelValue.mime)) {
                return props.baseUrl + '/' + props.modelValue.file
              } else {
                return getIconPath(props.modelValue.mime)
              }
            } else {
              return null
            }
          }
        } else {
          return null
        }
      },
      set: (val) => {
        if (val === null) {
          delete props.modelValue.file
        } else {
          props.modelValue.file = val
        }
      }
    })
    const error = computed(() => {
      if (!props.modelValue || !props.modelValue.errors) {
        return ''
      }
      const keys = Object.keys(props.modelValue.errors)
      const err = props.modelValue.errors[keys[0]]
      return Array.isArray(err) ? err[0] : err
    })
    const isFocus = ref(false)
    const onChange = (event: any) => {
      const allow_exts = new Array('jpg', 'png', 'xlsx', 'docx', 'pdf');
      uploadedFile.value = event.target.files[0]
      const extension = uploadedFile.value.name.split('.').pop()
      if(!allow_exts.some(e => e === extension)){
        DialogService.error({
          text: '.xls .docx .jpg .png .pdfのいずれかのファイルのみ許可されています'
        }, true)
        return
      }
      const reader = new FileReader();
      reader.onload = (e: any) => {
        thumnail.value = e.target.result;
        update()
      };
      if (uploadedFile.value !== null) {
        reader.readAsDataURL(uploadedFile.value);
      }
    }
    const update = () => {
      if (props.inputName && !fileName.value && uploadedFile.value && uploadedFile.value.name) {
        const splitname = uploadedFile.value.name.split('.')
        fileName.value = splitname[0]
      }
      context.emit('update:modelValue', props.modelValue)
    }
    const thumnailDelete = () => {
      uploadedFile.value = null
      thumnail.value = null
      if (props.inputName) {
        fileName.value = null
      }
      context.emit('update:modelValue', props.modelValue)
    }
    const onFocus = () => isFocus.value = true
    const onBlur = () => isFocus.value = false

    /**
     * Base64か判断します
     * @param str 
     * @returns 
     */
    const isBase64 = (str: string): boolean => {
      if (typeof str !== 'string') {
        return false
      }
      if (/^data:\w+\/[^;]+;base64,/.test(str)) {
        return true
      } else {
        const base64regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/
        return base64regex.test(str)
      }
    }

    const isImage = (mime: string): boolean => {
      return mime !== null && /^image\//.test(mime)
    }

    const getMimeByBase64 = (str: string): string|null => {
      if (typeof str !== 'string') {
        return null
      }
      const mimeRegex = /^data:(\w+\/[^;]+);base64,/
      const mime = str.match(mimeRegex)
      return (mime !== null) ? mime[1] : null
    }

    /**
     * アイコンのパスか判断します
     * @param str 
     * @returns 
     */
    const getIconPath = (mime: string): string => {
      if (mime === 'application/pdf') {
        return '/img/icon/upload-pdf.png'
      } else if (mime.indexOf('application/vnd.ms-excel') === 0 || mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        return '/img/icon/upload-excel.png'
      } else if (mime.indexOf('application/msword') === 0 || mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        return '/img/icon/upload-word.png'
      } else if (mime.indexOf('application/vnd.ms-powerpoint') === 0 || mime === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
        return '/img/icon/upload-powerpoint.png'
      } else {
        return '/img/icon/upload-file.png'
      }
    }
    
    return {
      uploadedFile,
      fileName,
      thumnail,
      error,
      isFocus,
      onChange,
      update,
      thumnailDelete,
      onFocus,
      onBlur
    }
  },
  components: {
    'input-text': InputText
  }
})
</script>

<style lang="scss" scoped>
@import '@scss/form/input-file.scss';
</style>